import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { parseError } from './utils/error-parser';
import { parseStackTrace, pinpointFrame } from './utils/stack-trace-parser';
import { loadManifest } from 'src/app/shared/utils/manifest';
import { ErrorDialogManagerService } from '@app/shared/errors/error-dialog-manager/error-dialog-manager.service';
import { ErrorDialogComponent } from '@app/shared/errors/error-dialog-manager/error-dialog/error-dialog.component';
import { env } from '../../../environments/env';

interface ErrorContextDto {
  userAgent: string;
  url: string;
}

interface ErrorSourceReferenceDto {
  filePath: string;
  functionName: string;
  lineNumber: number;
}

interface ErrorDto {
  name: string;
  message: string;
  stack: string;
  source: ErrorSourceReferenceDto;
  context: ErrorContextDto;
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class RemoteErrorLoggingService {
  constructor(
    private http: HttpClient,
    private errorDialogService: ErrorDialogManagerService,
  ) {}

  async logError(error: Error | string) {
    try {
      console.error(error);

      const parsedError = parseError(error);
      const { name, message, stack } = parsedError;

      const source = await this.getSourceReference(parsedError);
      const context = this.getContext();
      const version = this.getVersion();
      const payload: ErrorDto = {
        message,
        stack,
        name,
        source,
        context,
        version,
      };
      if (localStorage.getItem('show_error_dialog')) {
        this.errorDialogService.error(ErrorDialogComponent, {
          description: 'error.dialog.general_error.description',
          title: 'error.dialog.general_error.title',
          message: `Message: ${message}`,
          button: 'error.dialog.general_error.button',
        });
      }

      if (env.get().production) {
        await this.http.post('/api/error-report', payload).toPromise();
      }
    } catch {
      console.error('Unable to upload error to server');
    }
  }

  private async getSourceReference(error: Error): Promise<ErrorSourceReferenceDto> {
    const stackFrames = await parseStackTrace(error);

    if (!stackFrames || stackFrames.length === 0) {
      return null;
    }

    const mainStackFrame = await pinpointFrame(stackFrames[0]);

    return {
      filePath: mainStackFrame.fileName,
      functionName: mainStackFrame.functionName,
      lineNumber: mainStackFrame.lineNumber,
    };
  }

  private getContext(): ErrorContextDto {
    return {
      userAgent: window.navigator.userAgent,
      url: window.location.href,
    };
  }

  private getVersion(): string {
    const manifest = loadManifest();

    if (!manifest) {
      return 'dev';
    }

    return `${manifest.timestamp} - ${manifest.revision}`;
  }
}
